import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import type { ChargeRow } from './charges'

const api = clientFor('/ops/api')

/** 结算单状态：draft → confirmed → paid；仅 draft 可删除（删除时解除计费挂载） */
export type SettleStatus = 'draft' | 'confirmed' | 'paid'

/** 结算单：一个客户一个账期把多条 pending 计费明细归到一起。字段与 internal/settle/model.go 一致。 */
export interface Settlement {
  id: number
  tenant_id: number
  doc_no: string
  customer_id: number
  /** 账期，形如 2026-10 */
  period: string
  total_amount: number
  status: SettleStatus
  paid_at: string | null
  remark: string
  created_by: string
  created_at: string
  updated_at: string
}

/** 列表行：结算单 + 客户名 + 挂载的计费笔数 */
export interface ListRow extends Settlement {
  customer_name: string
  charge_count: number
}

/** 详情：单据 + 客户名 + 挂载的计费明细 */
export interface SettleDetail {
  settlement: Settlement
  customer_name: string
  charges: ChargeRow[]
}

export interface CreateSettleInput {
  customer_id: number
  /** 账期 YYYY-MM；不传后端按当月补 */
  period?: string
  /** 勾选的待结算明细；不传表示把该客户全部 pending 计费挂进来 */
  charge_ids?: number[]
  remark?: string
}

export interface SettleQuery extends PageQuery {
  keyword?: string
  customer?: number
  period?: string
  status?: string
}

/** 与后端 services/ops/internal/settle/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<SettleStatus, string> = {
  draft: '草稿',
  confirmed: '已确认',
  paid: '已付款',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<SettleStatus, 'info' | 'warning' | 'success'> = {
  draft: 'info',
  confirmed: 'warning',
  paid: 'success',
}

/** 金额合计：后端金额到分，这里同样四舍五入到分，避免浮点尾巴 */
export function sumAmounts(rows: ReadonlyArray<{ amount: number }>): number {
  let sum = 0
  for (const row of rows) sum += row.amount
  return Math.round(sum * 100) / 100
}

export const listSettlements = (q: SettleQuery): Promise<PageResult<ListRow>> =>
  api.get<PageResult<ListRow>>('/settlements', { ...q })

export const getSettlement = (id: number): Promise<SettleDetail> =>
  api.get<SettleDetail>(`/settlements/${id}`)

export const createSettlement = (input: CreateSettleInput): Promise<Settlement> =>
  api.post<Settlement>('/settlements', input)

/** 删除仅限草稿，后端同时把挂载的计费明细解挂回 pending */
export const deleteSettlement = (id: number): Promise<void> => api.del<void>(`/settlements/${id}`)

export const confirmSettlement = (id: number): Promise<void> =>
  api.post<void>(`/settlements/${id}/confirm`, {})

export const paySettlement = (id: number): Promise<void> =>
  api.post<void>(`/settlements/${id}/pay`, {})

/**
 * 导出 CSV：同一个路径带 export=csv，后端回 UTF-8 BOM 附件（Excel 打开不乱码）。
 * 导出鉴权是 ops:report:export，在后端处理器里细分，前端按钮同码控入口。
 */
export const exportSettlements = (q: Omit<SettleQuery, 'page' | 'size'>): Promise<Blob> =>
  api.getBlob('/settlements', { ...q, export: 'csv' })
